import { useLocalStorageState } from 'ahooks';
import { FormInstance, Form } from 'antd';
import { isEmpty, isPlainObject } from 'lodash-es';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getProductId, isAppleTV, isIOS, isVisionOS } from '@/infrastructure/product-info';
import { getCompareDateValue, isDataLabelingPlatform, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import { DenominatorTag } from '@/presentation/components/denominator-tag';
import { FormEntry, FormType, IFormRef, QueryForm } from '@/presentation/components/form';
import { NumberOperator } from '@/presentation/components/form/components/select-number';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import {
  INFERRED_LABELS_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getShowSwitchStatus,
  onChangeAppVersion,
} from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, iconText } from '@/presentation/components/hint-text/hintText';
import { DATA_LABELING_TIPS, numberFilterTips } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import { AppVersionOperator, FIELD_ALL, FIELD_LABEL, FieldKey, IOS_FOOM_LINKAGES_OPTION, Zone } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: FieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

const title = <>
  <div><span style={{ color: '#0062FF' }}>包含模式：</span>支持选择多个版本，表示满足多个指定版本的数据</div>
  <div><span style={{ color: '#0062FF' }}>大于等于模式：</span>{'只能选择一个版本，表示 >= 指定版本的数据'}</div>
  <div><span style={{ color: '#0062FF' }}>小于等于模式：</span>{'只能选择一个版本，表示 <= 指定版本的数据'}</div>
</>;

export const ProblemForm: FC<IProps> = (props) => {
  const { tokenInitValues } = props;
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [showSwitch, setShowSwitch] = useState(false);
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const config = useAppConfig();
  const { filterConfig } = useStores().ProductStore;
  const inferredLabelsConfig = getIssueFilterConfig(IssueType.ISSUE_TYPE_FOOM, filterConfig);

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;

      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;

      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        // 如果是详情页需要带上【无堆栈问题聚类方式】
        props.onValueInited?.({
          [FieldKey.IsInferenceFeature]: FilterEntry.FoomIOSDetail === props.entry
            ? props?.initialValue?.[FieldKey.IsInferenceFeature] : undefined,
          ...(form.getFieldsValue()),
        });
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);


  const labelTools = props.entry === FilterEntry.FoomIOSList ? [<DenominatorTag title={'支持联网下钻'}/>] : undefined;

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      labelTools,
      showTime:
        props.entry === FilterEntry.FoomIOSList
        || props.entry === FilterEntry.FoomIOSDetail,
      hiddenTimeWindow: props.entry !== FilterEntry.FoomIOSList,
      isIssuesTimeWindow: props.entry === FilterEntry.FoomIOSList,
      initTimeWindow: TimeType.MINUTE5,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      labelTools,
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.Foom,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      isSplit: true,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      labelTools: [iconText(title)],
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      label: FIELD_LABEL[FieldKey.OsVersion],
      fieldKey: FieldKey.OsVersion,
      fieldScope: Zone.Foom,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      labelTools,
      label: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.Foom,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.Foom,
      mode: 'tags',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      fieldKey: FieldKey.Brand,
      fieldScope: Zone.Foom,
      mode: 'tags',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.DeviceInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.Foom,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
      group: FormColumnGroup.UserInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: '消息ID',
      group: FormColumnGroup.exceptionInfo,
      initialDeleted: true,
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      initialDeleted: true,
      disabled: props.entry !== FilterEntry.FoomIOSList,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.UsedMem,
      label: 'FOOM前内存（MB）',
      group: FormColumnGroup.ContextInfo,
      initialDeleted: true,
      options: [{ value: NumberOperator.RANGE, label: '范围' }],
      disabled: ![FilterEntry.FoomIOSList, FilterEntry.FoomIOSDetail].includes(props.entry),
      processValue: (_result, allValues) => ({
        [FieldKey.UsedMem]:
          allValues[FieldKey.UsedMem]?.value || allValues[FieldKey.UsedMem],
        [FieldKey.UsedMemCompare]:
          allValues[FieldKey.UsedMemCompare]?.value
          || allValues[FieldKey.UsedMemCompare],
      }),
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: NumberOperator.RANGE,
          value,
        };
      },
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: '问题特征',
      initialDeleted: true,
      group: FormColumnGroup.exceptionInfo,
      disabled: props.entry !== FilterEntry.FoomIOSList,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      labelTools: [iconText(BUNDLE_TIPS_CASE), ...(labelTools || [])],
      group: FormColumnGroup.DeviceInfo,
      label: FIELD_LABEL[FieldKey.BundleId],
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.Foom,
      mode: 'single',
      initialValue: FIELD_ALL,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.ShiplyTag,
      labelTools: [iconText(ABTEST_TIPS), ...(labelTools || [])],
      group: FormColumnGroup.Custom,
      label: FIELD_LABEL[FieldKey.ShiplyTag],
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Foom,
      mode: 'tags',
      disabled: !config.enableShiplyAbTestTag,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'channelId',
      label: FIELD_LABEL[FieldKey.ChannelId],
      labelString: FIELD_LABEL[FieldKey.ChannelId],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.ChannelId,
      fieldScope: Zone.Foom,
      mode: 'tags',
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.AbTestTag,
      labelTools: [iconText(ABTEST_TIPS), ...(labelTools || [])],
      label: FIELD_LABEL[FieldKey.AbTestTag],
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Foom,
      mode: 'tags',
      group: FormColumnGroup.Custom,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.Linkages,
      labelString: '联动标记',
      group: FormColumnGroup.Tag,
      label: '联动标记',
      options: IOS_FOOM_LINKAGES_OPTION,
      mode: 'tags',
      disabled:
        ![FilterEntry.FoomIOSList, FilterEntry.FoomIOSDetail].includes(props.entry)
        || (!isIOS() && !isVisionOS() && !isAppleTV()),
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.TagsInFilter,
      group: FormColumnGroup.Tag,
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      initialValue: [FIELD_ALL],
      fieldScope: Zone.Foom,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.CharacterInput,
      name: FieldKey.StandardInferredLabels,
      group: FormColumnGroup.Tag,
      labelTools: [iconText(DATA_LABELING_TIPS)],
      label: '数据标记',
      labelString: '数据标记',
      mode: 'tags',
      options: inferredLabelsConfig?.labelOptions || [],
      initialValue: {
        mode: MatchMode.NOT,
        values: inferredLabelsConfig?.values?.map(item => item.value),
      },
      modeSelectProps: {
        defaultValue: MatchMode.NOT,
      },
      modeOptions: INFERRED_LABELS_OPTIONS,
      compareDisabled: true,
      disabled: !isDataLabelingPlatform(),
      initialDeleted: true,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      labelTools: [iconText(numberFilterTips)],
      label: '使用时长（秒）',
      labelString: '使用时长（秒）',
      group: FormColumnGroup.ContextInfo,
      options: NUMBER_MATCH_OPTIONS,
      initialDeleted: true,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      labelString: '地域',
      fieldScope: Zone.Foom,
      compareDisabled: true,
      initialDeleted: true,
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
      group: FormColumnGroup.Custom,
      initialDeleted: true,
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
      group: FormColumnGroup.Custom,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: '只显示新增问题',
      disabled: !showSwitch || props.entry !== FilterEntry.FoomIOSList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.Foom,
      initialValue: false,
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: 'buildNumber',
      labelTools: [iconText(ABTEST_TIPS)],
      label: FIELD_LABEL[FieldKey.BuildNumber],
      labelString: FIELD_LABEL[FieldKey.BuildNumber],
      fieldKey: FieldKey.BuildNumber,
      fieldScope: Zone.Foom,
      mode: 'tags',
      initialDeleted: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.OriginalStacks,
      label: '有无内存分配堆栈',
      fieldKey: FieldKey.OriginalStacks,
      fieldScope: Zone.Foom,
      mode: 'single',
      initialValue: undefined,
      compareDisabled: true,
      initialDeleted: true,
      disabled: ![FilterEntry.FoomIOSDetail].includes(props.entry),
    },
  ];
  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    // 如果是详情页需要带上【无堆栈问题聚类方式】
    props.onFinish({
      [FieldKey.IsInferenceFeature]: FilterEntry.FoomIOSDetail === props.entry
        ? props?.initialValue?.[FieldKey.IsInferenceFeature] : undefined,
      ...value,
      ...compareDateValue });
  };
  return <QueryForm
    formProps={{
      isKeyPressSubmit: false,
    }}
    cachePrefix={'prefix5'}
    ref={tableRef}
    form={form}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_FOOM}
    hasCompare={props.entry === FilterEntry.FoomIOSList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
};
